import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import dayjs from 'dayjs';
import { ICareMap, ILightCareMap } from '../caremap/ICareMap';
import { WithLinks } from '../caremap/WithLinks';
import { IInsight, InsightConfidence, InsightType } from '../insights/IInsight';
import { InsightsModel } from '../insights/InsightsModel';
import { CaremapInsightsModelFactory } from '../insights/CaremapInsightsModelFactory';
import { Logger } from '../support/Logger';
import { OrganizationsState } from './OrganizationsState';

const CAREMAP_MEDIA_TYPE = 'application/vnd.innerhive.caremap.v3+json';

// A book of clients is read two requests deep (the care map, then its insights), so the fan-out is
// bounded rather than fired all at once: a fifty-client caseload would otherwise open a hundred
// connections at page load and starve everything else the home is fetching.
const MAX_CONCURRENT_CLIENTS = 6;

const CONFIDENCE_RANK: { [key in InsightConfidence]: number } = {
    high: 3,
    medium: 2,
    low: 1
};

// Which kind of finding a case manager needs to see first. "Stands out" and "worth checking" each
// describe something unresolved about the client; a trend or a suggestion is context that keeps until
// they open them. Only one finding is shown per client on the caseload, so this decides which one.
const TYPE_RANK: { [key in InsightType]: number } = {
    anomaly: 3,
    gap: 2,
    recommendation: 1,
    trend: 0
};

// How loudly one finding asks to be looked at. Type leads because it is what the caseload row shows,
// and confidence separates findings of the same kind. The API deploys separately from the app, so a
// type or confidence this build does not know sorts with the quietest rather than ahead of anything.
const urgency = (insight: IInsight): number => {
    return (TYPE_RANK[insight.type] ?? 0) * 10 + (CONFIDENCE_RANK[insight.confidence] ?? 0);
};

/**
 * The client's name as the organization's care map list gives it.
 *
 * Read defensively and with a fallback because the list is a projection: the patient node is not
 * guaranteed, and a client this organization cannot read privately is the likeliest one to arrive
 * without it. A row that says "Unnamed client" is still actionable; a row that says "undefined" is
 * a bug report.
 */
const nameOf = (careMap: { patient?: { firstName?: string; lastName?: string } }): string => {
    const patient = careMap.patient;
    return [patient?.firstName, patient?.lastName].filter(Boolean).join(' ') || 'Unnamed client';
};

export interface IClientInsight {
    insight: IInsight;
    careMapId: string;
    clientName: string;
}

/**
 * Why a client's findings could not be read. Three reasons, because each one asks something
 * different of the reader and only one of them is worth trying again.
 *
 * 'not-shared' is a client nobody at this organization holds a share on. Somebody has to grant
 * access before there is anything here at all.
 *
 * 'view-only-access' is a client the organization can open but not read privately. The share
 * exists, so telling this reader the client is not shared would send them chasing the wrong thing;
 * what they need is a fuller level of access.
 *
 * 'request-failed' is a read that should have worked. Trying again is exactly the right response.
 */
export type UnreadableReason = 'not-shared' | 'view-only-access' | 'request-failed';

export interface IUnreadableClient {
    careMapId: string;
    /** Carried so the tab can name the client rather than only counting them. */
    clientName: string;
    reason: UnreadableReason;
}

/**
 * The outcome of reading one client, named rather than encoded in the shape of the return.
 *
 * Named because an empty list of findings is ambiguous and the ambiguity is expensive: a client
 * with nothing outstanding and a client this organization cannot read both produce one, and only
 * the first may be counted into the figures. Counting the second draws a full-score, steady cell
 * for somebody nobody has looked at.
 *
 * Discriminated on a string rather than a boolean because the specs typecheck this file under
 * test/tsconfig.json, which does not set `strict`, and a boolean discriminant does not narrow
 * without strictNullChecks.
 */
type ClientReadResult =
    | { kind: 'readable'; insights: IClientInsight[] }
    | { kind: 'unreadable'; reason: UnreadableReason };

/** The same outcome once a thrown read has been folded in and the client has been named. */
type ClientOutcome =
    | { kind: 'readable'; insights: IClientInsight[] }
    | { kind: 'unreadable'; client: IUnreadableClient };

// The caseload roll-up behind the agency home's attention list. Each client's insights are read
// through the same InsightsModel a single care map uses, so dismissal, feedback and the response
// shape stay in one place; this class only merges and orders what those models hold.
export class OrganizationInsightsState extends State<Record<string, never>> {
    static inject = () => [
        OrganizationsState,
        CaremapInsightsModelFactory,
        APIClient,
        Logger
    ];
    constructor(
        private orgState: OrganizationsState,
        private insightsFactory: CaremapInsightsModelFactory,
        private apiClient: APIClient,
        private logger: Logger
    ) {
        super({});
    }

    private entries: IClientInsight[] = [];

    private loadedOrgId?: string;
    private inFlight?: Promise<void>;

    // Which read is the current one. A fan-out takes as long as the slowest of its clients, so two
    // can overlap whenever the organization changes or a retry starts, and the one that finishes
    // last is not necessarily the one that should be on screen.
    private generation = 0;

    hasLoaded = false;

    // Clients whose insights could not be read. Held with a name and a reason rather than as a
    // count, because the tab leaves them out of every figure and a reader who cannot see which
    // clients those are has no way to act on the gap. Counted but still drawn, an unreadable client
    // renders as a full-score, steady cell, which states something about a person nobody knows.
    private unreadable: IUnreadableClient[] = [];

    getUnreadableClients = (): IUnreadableClient[] => [...this.unreadable];

    /**
     * Read the caseload again, for the retry a failed client offers.
     *
     * Goes back to the server where load() would hand back what it already has. Scoped to the
     * organization rather than to the one client because the figures are built from the caseload as
     * a whole, and at six concurrent clients re-reading all of them is cheap enough not to warrant a
     * second path through the same code.
     *
     * Deliberately leaves `hasLoaded` alone. Clearing it would drop the whole tab to its skeleton
     * for the length of the retry, and leave it there for good if the retry threw.
     */
    reload = async (orgId: string) => {
        // Joined rather than raced: two fan-outs for one caseload would both publish, and the
        // reader pressing retry is asking for one fresh answer.
        if (this.inFlight) {
            await this.inFlight;
        }

        // The reader may have left for another organization while that one finished, and the
        // generation counter only knows which call is newest, not which caseload is wanted.
        if (this.loadedOrgId !== orgId) {
            return;
        }

        // A retry is also the one moment the caseload itself is worth re-reading: a client added or
        // granted access since the first load is otherwise absent from this read's input entirely.
        await this.orgState.loadOrganizationCareMaps(orgId);
        await this.runLoad(orgId);
    };

    load = async (orgId: string) => {
        if (this.inFlight && this.loadedOrgId === orgId) {
            await this.inFlight;
            return;
        }

        // A completed load is kept, not repeated. Each client costs two requests, so without this a
        // reader moving between the agency tabs re-runs the whole fan-out every time they come back.
        if (this.hasLoaded && this.loadedOrgId === orgId) {
            return;
        }

        // A different organization's findings are not a stale version of this one's, they are the
        // wrong data. Clearing before the fetch, and publishing the clear, means the tab shows its
        // loading state rather than pairing one org's caseload with another's findings.
        if (this.loadedOrgId !== orgId) {
            this.hasLoaded = false;
            this.entries = [];
            this.unreadable = [];
            this.setState({});
        }

        await this.runLoad(orgId);
    };

    private runLoad = async (orgId: string) => {
        this.loadedOrgId = orgId;
        const generation = ++this.generation;
        const thisLoad = this.loadInternal({ orgId, generation });
        this.inFlight = thisLoad;

        try {
            await thisLoad;
        } finally {
            // Only the current load may clear the marker. A superseded one clearing it would let the
            // next call start a second fan-out for an organization already being read.
            if (this.inFlight === thisLoad) {
                this.inFlight = undefined;
            }
        }
    };

    /**
     * One client's outcome, with a thrown read turned into an outcome like any other.
     *
     * A 403 is the server telling this organization the client is not theirs, which is a permission
     * fact rather than a fault, so it is neither retried nor filed with Sentry. It is rare: a self
     * link means some member of staff holds a share, and a share held by ANY member gives every
     * member a role (see the api's role resolution), so reaching here means the last share went
     * away between the list read and this one. Anything else that throws is a real failure, worth
     * both the retry and the report.
     */
    private readOneClient = async (lightCareMap: ILightCareMap): Promise<ClientOutcome> => {
        const unreadable = (reason: UnreadableReason): ClientOutcome => ({
            kind: 'unreadable',
            client: {
                careMapId: lightCareMap.careMapId,
                clientName: nameOf(lightCareMap),
                reason
            }
        });

        try {
            const result = await this.loadForCareMap(lightCareMap);

            if (result.kind === 'readable') {
                return result;
            }

            // A permission gap is not reported to Sentry, and neither is a failed read: the
            // insights model already logged that one with the cause in the message, and by the
            // time it reaches here it is a reason with no error behind it, so a second report
            // would name no cause at all.
            return unreadable(result.reason);
        } catch (err: any) {
            // Matched on the api's own refusal rather than on the status alone: a 403 from a gateway
            // or a proxy is not the server saying this client is not theirs, and treating it as one
            // would retire a retryable failure into a permanent, unlogged permission row.
            if (err?.status === 403 && err?.errorCode === 'not-authorized') {
                return unreadable('not-shared');
            }

            this.logger.error(err, {
                context: 'OrganizationInsightsState failed loading insights for a client',
                careMapId: lightCareMap.careMapId
            });
            return unreadable('request-failed');
        }
    };

    private loadInternal = async ({ orgId, generation }: { orgId: string; generation: number }) => {
        let careMaps = this.orgState.getOrganizationCareMaps(orgId);
        if (careMaps.length === 0) {
            await this.orgState.loadOrganizationCareMaps(orgId);
            careMaps = this.orgState.getOrganizationCareMaps(orgId);
        }

        const collected: IClientInsight[] = [];
        const unreadable: IUnreadableClient[] = [];

        const queue = [...careMaps];
        const readNextClient = async () => {
            while (queue.length > 0) {
                const lightCareMap = queue.shift();
                if (!lightCareMap) {
                    return;
                }

                // One unreadable client must not blank the whole caseload, so every outcome is
                // recorded and the loop carries on; the tab then names them rather than presenting
                // a short list as the whole book.
                const outcome = await this.readOneClient(lightCareMap);
                if (outcome.kind === 'readable') {
                    collected.push(...outcome.insights);
                } else {
                    unreadable.push(outcome.client);
                }
            }
        };

        const workerCount = Math.min(MAX_CONCURRENT_CLIENTS, queue.length);
        await Promise.all(Array.from({ length: workerCount }, readNextClient));

        // Only the newest read may publish. Two can be in flight whenever the organization changes
        // or a retry starts, they finish in whatever order their slowest client allows, and an
        // older one landing last would put the wrong caseload on screen under the current name.
        if (this.generation !== generation) {
            return;
        }

        this.entries = collected;
        // By name, not by whichever of six workers finished first: these are people, and a list of
        // them reshuffling between one read and the next reads as though something changed.
        this.unreadable = unreadable.sort((a, b) => a.clientName.localeCompare(b.clientName));
        this.hasLoaded = true;
        this.setState({});
    };

    private loadForCareMap = async (lightCareMap: ILightCareMap): Promise<ClientReadResult> => {
        // A care map NONE of this organization's staff hold a share on comes back without a self
        // link, which is the same signal the Clients tab reads to say it is not shared. There is
        // nothing readable behind it and no amount of retrying changes that, so it is a permission
        // gap rather than a failure.
        if (!lightCareMap.links.self) {
            return { kind: 'unreadable', reason: 'not-shared' };
        }

        // The organization's care map list is a light projection that carries no insights link, so
        // the care map is re-read in full to get the link the insights model follows.
        const careMap: WithLinks<ICareMap> = await this.apiClient.get(lightCareMap.links.self, CAREMAP_MEDIA_TYPE);

        // The second shape a permission gap takes, and the one that hides. A view-only share is
        // enough to open the care map and not enough to read what is recorded on it, so the server
        // withholds the insights link. InsightsModel treats that as "no insights" and reports a
        // successful load of an empty list, which is right for the client's own screen, where it
        // renders an empty state, and wrong across a caseload, where it cannot be told apart from a
        // client with nothing outstanding. Checked before the model runs, so this does not depend
        // on reading an absence back out of it.
        if (!careMap.links.insights) {
            return { kind: 'unreadable', reason: 'view-only-access' };
        }

        const model = new InsightsModel(careMap, this.insightsFactory);
        await model.load();

        // The model catches its own fetch failure and resolves with an empty list so a single client's
        // screen can offer a retry. Read across a caseload that is indistinguishable from a client who
        // genuinely has nothing outstanding, and the tab would then describe a short list as the whole
        // book, so say which one this is rather than returning the same empty list for both.
        if (model.loadFailed) {
            return { kind: 'unreadable', reason: 'request-failed' };
        }

        // The same builder the unreadable rows use, so a client with no name on file does not read
        // as "Unnamed client" on one surface and as a blank on another.
        const clientName = nameOf(careMap);

        // Only the insight and who it belongs to are kept. Holding the model on every entry pinned
        // one InsightsModel per care map for the life of the caseload, for nothing that reads it.
        return {
            kind: 'readable',
            insights: model.getActiveAboutCareMap().map(insight => ({
                insight,
                careMapId: lightCareMap.careMapId,
                clientName
            }))
        };
    };

    // Most urgent first, then most recently noticed. Insights noticed in the same pass are
    // separated by how recent the evidence behind them is, so the freshest observation leads.
    getAttentionItems = (): IClientInsight[] => {
        return [...this.entries].sort((a, b) => {
            const byUrgency = urgency(b.insight) - urgency(a.insight);
            if (byUrgency !== 0) {
                return byUrgency;
            }

            const byNoticed = dayjs(b.insight.createdAt).valueOf() - dayjs(a.insight.createdAt).valueOf();
            if (byNoticed !== 0) {
                return byNoticed;
            }

            return dayjs(b.insight.dateRangeTo).valueOf() - dayjs(a.insight.dateRangeTo).valueOf();
        });
    };

    // What one client has outstanding, most urgent first. The caseload reads by client rather than by
    // finding, so a client's observations are fetched together and shown under their own name, and
    // the one it leads with is the first of these.
    getItemsForCareMap = (careMapId: string): IClientInsight[] => {
        return this.getAttentionItems().filter(entry => entry.careMapId === careMapId);
    };

    // The most recent evidence behind anything outstanding for this client, shown on their row so
    // the reader can tell a fresh flag from a stale one. Empty when nothing is outstanding.
    getLatestActivity = (careMapId: string): string | undefined => {
        const items = this.getItemsForCareMap(careMapId);
        if (items.length === 0) {
            return undefined;
        }
        return items.map(entry => entry.insight.dateRangeTo).sort().pop();
    };
}
